import React, { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { Bookmark, Loader2, Pencil, Search, Trash2 } from 'lucide-react';
import { Filterset } from '@/app/services/filtersetService';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from './ConfirmationModal';
import { useToast } from '@/shared/contexts/ToastContext';
import { Input } from './Input';
import { AppliedFilterEditor } from './AppliedFilterEditor';
import { sanitizeFilterExpression } from '@/shared/utils/filterUtils';

/** Fallback href builder — used when getFiltersetUrl is not provided. */
const buildFiltersetHref = (filterset: Filterset): string => {
  const params = new URLSearchParams(sanitizeFilterExpression(filterset.expression ?? ''));
  // Normalise bare keys to q.* (stored expressions may omit the prefix)
  const qParams = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    qParams.append(key.startsWith('q.') ? key : `q.${key}`, value);
  }
  if (filterset.id) qParams.set('q.user_filter', String(filterset.id));
  const search = qParams.toString().replace(/%7E/gi, '~');
  return search ? `${window.location.pathname}?${search}` : window.location.pathname;
};

export interface SavedFiltersProps {
  isOpen: boolean;
  onToggle: () => void;
  savedFilters: Filterset[] | undefined;
  isLoading: boolean;
  onApplyFilterset: (filterset: Filterset) => void;
  onDelete?: (id: number) => void | Promise<void> | undefined;
  onUpdate?: (id: number, data: Partial<Filterset>) => Promise<void>;
  activeFiltersetId?: number | null;
  currentFilterExpression?: string;
  showSearch?: boolean;
  /**
   * Optional URL builder. When provided it is used to build the <a href>;
   * source it from `useFilterManagement().getFiltersetUrl`.
   *
   * When omitted, the component builds the href internally from the
   * filterset's stored expression so every row is always an <a> — no
   * prop threading required for new call-sites.
   *
   * Plain click → applies in current tab. Cmd/Ctrl/middle-click → new tab.
   */
  getFiltersetUrl?: (filterset: Filterset) => string;
}

export const SavedFilters: React.FC<SavedFiltersProps> = ({
  isOpen,
  onToggle,
  savedFilters,
  isLoading,
  onApplyFilterset,
  onDelete,
  onUpdate,
  activeFiltersetId,
  currentFilterExpression,
  showSearch = false,
  getFiltersetUrl,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState<Filterset | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const normalizedActiveFiltersetId = activeFiltersetId != null ? Number(activeFiltersetId) : null;

  const openDeleteConfirm = (filter: Filterset) => {
    if (!filter.id || !onDelete || isDeleting) {
      return;
    }

    setFilterToDelete(filter);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) {
      return;
    }

    setFilterToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!filterToDelete?.id || !onDelete || isDeleting) {
      return;
    }

    const deletedName = filterToDelete.name;
    try {
      setIsDeleting(true);
      await Promise.resolve(onDelete(filterToDelete.id));
      setFilterToDelete(null);
      showToast(
        t('common.savedFilterDeleted', { defaultValue: 'Filter "{{name}}" deleted successfully', name: deletedName }),
        'success',
      );
    } catch {
      showToast(
        t('common.savedFilterDeleteError', { defaultValue: 'Failed to delete filter "{{name}}"', name: deletedName }),
        'error',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`border-t border-border bg-surface flex-shrink-0 sticky bottom-0 ${isOpen ? 'flex flex-col' : ''}`}>
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-alt flex-shrink-0"
        onClick={onToggle}
        disabled={isDeleting}
      >
        <span className="text-sm text-text flex items-center gap-2">
          <Bookmark className="w-4 h-4 text-primary" />
          {t('common.savedFilters', 'Saved Filters')}
        </span>
        <FaChevronDown size={14} className={`text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="p-2 bg-surface-alt overflow-y-auto max-h-[calc(100vh-14rem)]">
          {showSearch && (
            <div className="relative mb-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted pointer-events-none" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('common.searchSavedFilters', 'Search filters...')}
                className="w-full pl-7 pr-2 py-1 text-sm border border-border-strong rounded bg-surface text-text placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="saved-filters-search-input"
              />
            </div>
          )}
          {(isLoading || isDeleting) ? (
            <div className="text-sm text-text-muted px-2 py-1">
              {isDeleting ? t('common.deleting', 'Deleting...') : t('common.loading', 'Loading...')}
            </div>
          ) : (
            <ul>
              {savedFilters && savedFilters.filter((filter) =>
                !showSearch || filter.name.toLowerCase().includes(searchTerm.toLowerCase())
              ).map((filter) => (
                <li key={filter.id} className="flex items-center justify-between py-1 group min-w-0">
                  {/* getFiltersetUrl (explicit prop) takes precedence; otherwise href is
                      built internally from the filterset expression — no prop threading needed. */}
                  <a
                    href={(getFiltersetUrl ?? buildFiltersetHref)(filter)}
                    className={`flex-grow min-w-0 text-left text-sm px-2 py-1.5 hover:bg-muted rounded block ${normalizedActiveFiltersetId === Number(filter.id) ? 'text-primary font-medium bg-primary/5 border border-primary' : 'text-text'} ${isDeleting ? 'pointer-events-none opacity-50' : ''}`}
                    onClick={(e) => {
                      if (isDeleting) {
                        e.preventDefault();
                        return;
                      }
                      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                        // Let browser handle modifier/middle-click → new tab/window.
                        return;
                      }
                      e.preventDefault();
                      onApplyFilterset(filter);
                      onToggle();
                    }}
                    onAuxClick={(e) => {
                      // Middle-click sometimes surfaces only as auxclick
                      // (button=1) rather than click. Letting the default
                      // run opens a new tab natively.
                      if (e.button === 1 && !isDeleting) {
                        // No-op — browser handles it.
                      }
                    }}
                    aria-disabled={isDeleting || undefined}
                    title={filter.name}
                    data-testid={`saved-filter-link-${filter.id}`}
                  >
                    <span className="block truncate">{filter.name}</span>
                    {filter.shared && (
                      <span className="block text-xs text-text-muted font-normal">
                        ({t('common.shared', 'Shared')})
                      </span>
                    )}
                  </a>
                  <div className={`flex items-center pl-2 ${normalizedActiveFiltersetId === Number(filter.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                    {onUpdate && (
                      <AppliedFilterEditor
                        filterset={filter}
                        onUpdate={onUpdate}
                        currentExpression={
                          normalizedActiveFiltersetId === Number(filter.id)
                            ? currentFilterExpression
                            : undefined
                        }
                        renderTrigger={(openModal) => (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal();
                            }}
                            disabled={isDeleting}
                            className="mr-2 text-primary hover:underline text-[14px] font-normal disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={`Edit filter ${filter.name}`}
                          >
                            <Pencil size={16} className="text-arrowIcon hover:text-primary" />
                          </button>
                        )}
                      />
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteConfirm(filter);
                        }}
                        disabled={isDeleting}
                        className="p-1 text-text-muted hover:text-danger rounded-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={`Delete filter ${filter.name}`}
                      >
                        {isDeleting && filterToDelete?.id === filter.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    )}
                  </div>
                </li>
              ))}
              {(!savedFilters || savedFilters.length === 0 || (showSearch && searchTerm && !savedFilters.some(f => f.name.toLowerCase().includes(searchTerm.toLowerCase())))) && (
                <li className="text-sm text-text-muted px-2 py-1">
                  {showSearch && searchTerm && savedFilters && savedFilters.length > 0
                    ? t('common.noMatchingFilters', 'No matching filters.')
                    : t('common.noSavedFilters', 'No saved filters.')}
                </li>
              )}
            </ul>
          )}
        </div>
      )}
      <ConfirmationModal
        isOpen={!!filterToDelete}
        onClose={closeDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirmDeletion', 'Confirm Deletion')}
        message={t('common.confirmDeleteSavedFilter', {
          defaultValue: 'Are you sure you want to delete the saved filter "{{name}}"?',
          name: filterToDelete?.name || '',
        })}
        confirmText={t('common.delete', 'Delete')}
        cancelText={t('common.cancel', 'Cancel')}
        isLoading={isDeleting}
      />
    </div>
  );
};
